import React, { memo, useState } from 'react';

import { useRecoilState, useSetRecoilState } from 'recoil';

import MinusIcon from '@/components/common/OptionButton/icons/Minus/MinusIcon';

import OptionButton from '@/components/common/OptionButton/OptionButton';
import { SelectedRow } from '@/pages/ContractLines/hooks/useGridSelectionSubscription';
import { UsageLineSelectionPayload, clearLinesSelection } from '../../UsageDetails/usageDetailsSelectionObservable';
import useIgnoreUsageDetails from '../../hooks/useIgnoreUsageDetails';
import { UsageDetailDataInputType } from '@/graphql/contracts/_generated_/contracts';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { checkedIds } from '../../UsageDetails/checkedIds.state';
import { rightsidePanel_isOpen } from '../RightPanel.state';
import { TabKeys } from '@/pages/ImportingTool/types';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import { FormattingUtils } from '@/utils/Formatting.utils';

interface IIgnoreContainerProps {
  selectedUsageDetails: SelectedRow<UsageLineSelectionPayload>[];
  onIgnoreAction: (tabName: TabKeys) => void;
}

function IgnoreStatusContainer({ selectedUsageDetails, onIgnoreAction }: IIgnoreContainerProps) {
  const { ignoreUsageDetails } = useIgnoreUsageDetails();
  const [selectedRowIds, setSelectedRowIds] = useRecoilState(checkedIds);
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [ignoreComment, setIgnoreComment] = useState('');
  const usageHeaderId = selectedUsageDetails.length > 0 &&
    selectedUsageDetails[0].payload.usageDetailsLine.usageHeaderID;

  const selectedUsageDetailsIds = selectedRowIds?.reduce((ids: UsageDetailDataInputType[], id) =>
    [...ids, { usageDetailId: id }], []);

  const handleIgnoreUsageDetails = () => {
    if (usageHeaderId) {
      ignoreUsageDetails(usageHeaderId, selectedUsageDetailsIds, ignoreComment || undefined).then((result) => {
        if (result?.usageHeaderID) {
          onIgnoreAction(TabKeys.IGNORED);
          setSelectedRowIds([]);
          clearLinesSelection();
          setIsOpenRightPanel(false);
          setIgnoreComment('');
        }
      });
    }
  };

  if (selectedUsageDetails.length === 0) {
    return null;
  }

  return (
    <>
      <AgConfirmationModal
        isOpen={showConfirmationModal}
        contentStyle={{ width: 650 }}
        onClose={() => {
          setShowConfirmationModal(false);
          setIgnoreComment('');
        }}
        onConfirm={handleIgnoreUsageDetails}
        title="Lines to be irreversibly excluded from Processing"
      >
        You are about to exclude the selected usage records from further processing of the current Usage Header.<br />
        You can restore previously ignored records in case usage header is not being currently processed,
        or its status is not Abandoned, SO Generated or Partially Processed. <br />
        Please, confirm if you would like to proceed.

        <div className="mt-15">
          <ModalFormTextarea
            id="ignoreComment"
            labelTitle="Comment"
            placeholder="Enter reason for ignoring these lines"
            value={ignoreComment}
            onChange={(event) => {
              const value = FormattingUtils.replaceControlCharacters(event.target.value)
                ? event.target.value
                : '';
              setIgnoreComment(value);
            }}
          />
        </div>
      </AgConfirmationModal>
      <OptionButton
        onClick={() => setShowConfirmationModal(true)}
        Icon={<MinusIcon variant='ignore'/>}
        className={'m-0'}
      >
        Ignore lines for processing
      </OptionButton>
    </>
  );
}

export default memo(IgnoreStatusContainer);
